import * as THREE from 'three';
import type { MasterLightingConfig, StyleConfig } from './StyleConfig';
import {
  aerialPerspectiveTint,
  dimHex,
  environmentIntensityByElevation,
  fillIntensityFromSun,
  fogDensityByElevation,
  hemiIntensityByElevation,
  horizonHueShiftByAtmosphere,
  multiplyHex,
  sunColorByElevation,
  sunIntensityByElevation,
} from './LightingCurves';

export interface DerivedLighting {
  sunDir: THREE.Vector3;
  secondarySunDir: THREE.Vector3;
  sunColorHex: string;
  sunIntensity: number;
  fillIntensity: number;
  hemiSkyHex: string;
  hemiGroundHex: string;
  hemiIntensity: number;
  environmentIntensity: number;
  fogColorHex: string;
  fogDensity: number;
  bounceColorHex: string;
  rimColorHex: string;
  horizonHueShift: number;
}

const GROUND_ALBEDO_HEX = '#c89870';

export function solveLighting(
  master: MasterLightingConfig,
  sky: StyleConfig['sky'],
  overrides: Partial<DerivedLighting> = {},
): DerivedLighting {
  const elevation = master.sunElevationDeg;
  const atmosphere = master.atmosphere;
  const sunDir = directionFromAngles(elevation, master.sunAzimuthDeg);
  const secondaryElevation = elevation + master.secondarySun.relativeElevationDeg;
  const secondaryAzimuth = master.sunAzimuthDeg + master.secondarySun.relativeAzimuthDeg;
  const secondarySunDir = directionFromAngles(secondaryElevation, secondaryAzimuth);
  const sunColorHex = sunColorByElevation(elevation, atmosphere.warmthBias);
  const sunIntensity = sunIntensityByElevation(elevation);

  const derived: DerivedLighting = {
    sunDir,
    secondarySunDir,
    sunColorHex,
    sunIntensity,
    fillIntensity: master.secondarySun.enabled
      ? fillIntensityFromSun(sunIntensity, master.secondarySun.intensity)
      : 0,
    hemiSkyHex: sky.zenithHex,
    hemiGroundHex: dimHex(sky.horizonHex, 0.4),
    hemiIntensity: hemiIntensityByElevation(elevation),
    environmentIntensity: environmentIntensityByElevation(elevation),
    fogColorHex: aerialPerspectiveTint(sky, sunColorHex, elevation, atmosphere.turbidity),
    fogDensity: fogDensityByElevation(elevation, atmosphere.hazeBoost, atmosphere.turbidity),
    bounceColorHex: multiplyHex(sunColorHex, GROUND_ALBEDO_HEX),
    rimColorHex: sky.oppositeHorizonHex,
    horizonHueShift: horizonHueShiftByAtmosphere(elevation, atmosphere.warmthBias, atmosphere.turbidity),
  };
  return { ...derived, ...overrides };
}

export function directionFromAngles(elevationDeg: number, azimuthDeg: number): THREE.Vector3 {
  const elevation = THREE.MathUtils.degToRad(elevationDeg);
  const azimuth = THREE.MathUtils.degToRad(azimuthDeg);
  const horizontal = Math.cos(elevation);
  return new THREE.Vector3(
    Math.sin(azimuth) * horizontal,
    Math.sin(elevation),
    Math.cos(azimuth) * horizontal,
  ).normalize();
}
